/**
 * Currency formatting utilities.
 *
 * Resolves the display currency based on:
 * 1. User's explicit preference (preferredCurrency from settings)
 * 2. Ad account's native currency (from Google/Meta API)
 * 3. Fallback to PLN
 */

export type CurrencyCode = "PLN" | "USD" | "EUR" | string;

const LOCALE_MAP: Record<string, string> = {
  PLN: "pl-PL",
  USD: "en-US",
  EUR: "de-DE",
};

function getLocale(currency: string): string {
  return LOCALE_MAP[currency] ?? "pl-PL";
}

export function resolveDisplayCurrency(
  userPreference: string | null | undefined,
  accountCurrency: string | null | undefined
): CurrencyCode {
  if (userPreference) return userPreference;
  if (accountCurrency) return accountCurrency;
  return "PLN";
}

export function formatCurrency(value: number, currency: CurrencyCode = "PLN"): string {
  return new Intl.NumberFormat(getLocale(currency), {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Short currency format — no decimals, for compact tables */
export function formatCurrencyShort(value: number, currency: CurrencyCode = "PLN"): string {
  return new Intl.NumberFormat(getLocale(currency), {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/** Format currency for daily table — shows "—" for zero */
export function formatCurrencyDash(value: number, currency: CurrencyCode = "PLN"): string {
  if (!value || value === 0) return "—";
  return formatCurrency(value, currency);
}

export function getCurrencySymbol(currency: CurrencyCode): string {
  const formatted = new Intl.NumberFormat(getLocale(currency), {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(0);
  return formatted.replace(/[\d\s.,]/g, "").trim();
}

/** Get short currency label for UI labels, e.g. "(USD)" or "(PLN)" */
export function currencyLabel(currency: CurrencyCode): string {
  return `(${currency})`;
}
